import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { apiError, useAuth } from "@/context/AuthContext";
import { StatusBadge, PriorityBadge, fmtDate } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  CaretLeft, Clock, PaperPlaneRight, Paperclip, GitBranch, TreeStructure,
  ChatCircleDots, ClockClockwise, ArrowsClockwise, Check, X, Warning,
} from "@phosphor-icons/react";

const inputCls = "bg-zinc-950 border-zinc-800 rounded-sm text-zinc-100 focus:border-emerald-600 focus-visible:ring-emerald-600";
const label = "text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1.5 block";

const viewAttachmentFile = (a, toast) => {
  const w = window.open("about:blank", "_blank"); // open synchronously so browsers don't block the popup
  try {
    const parts = a.data.split(",");
    const mime = (parts[0].match(/data:(.*?);/) || [])[1] || a.type || "application/octet-stream";
    const bin = atob(parts[1]);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
    const url = URL.createObjectURL(new Blob([arr], { type: mime }));
    if (w && !w.closed) w.location.href = url; else window.open(url, "_blank");
  } catch (err) {
    if (w && !w.closed) w.close();
    toast.error("Could not open this file");
  }
};

const STATUS_ACTIONS = {
  member: [
    { s: "acknowledged", l: "Acknowledge" }, { s: "in_progress", l: "Start Work" },
    { s: "on_hold", l: "Pause / Hold" }, { s: "blocked", l: "Mark Blocked" },
    { s: "waiting_material", l: "Waiting Material" }, { s: "submitted", l: "Submit for Review" },
  ],
};

function Meta({ label: l, children }) {
  return (
    <div className="py-2.5 border-b border-zinc-800 last:border-0">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-0.5">{l}</div>
      <div className="text-sm text-zinc-200">{children}</div>
    </div>
  );
}

export default function TaskDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [t, setT] = useState(null);
  const [users, setUsers] = useState([]);
  const [comment, setComment] = useState("");
  const [dialog, setDialog] = useState(null); // feedback|extension|reassign
  const [fb, setFb] = useState({ progress: 0, completed: "", pending: "", time_spent: "", blockers: "", support_required: "", next_action: "", remarks: "" });
  const [ext, setExt] = useState({ requested_due: "", reason: "", pending_work: "" });
  const [reassign, setReassign] = useState({ new_owner: "", reason: "" });
  const [ef, setEf] = useState({});

  const load = useCallback(() => {
    api.get(`/tasks/${id}`).then((r) => setT(r.data)).catch(() => toast.error("Task not found"));
  }, [id]);

  useEffect(() => { load(); api.get("/users?active=true").then((r) => setUsers(r.data)); }, [load]);
  const [sp, setSp] = useSearchParams();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (t && sp.get("edit") === "1") { openTaskEdit(); setSp({}, { replace: true }); } }, [t]);

  if (!t) return <div className="p-8 text-zinc-500 font-mono text-sm">Loading task…</div>;

  const isManager = user.role !== "member";
  const isAssignee = (t.assignee_ids || []).includes(user.id);
  const myAssignee = (t.assignees || []).find((a) => a.user_id === user.id);

  const changeStatus = async (s, note) => {
    try {
      await api.post(`/tasks/${id}/status`, { status: s, note });
      toast.success(`Status: ${s.replace(/_/g, " ")}`);
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const submitFeedback = async () => {
    try {
      await api.post(`/tasks/${id}/feedback`, { ...fb, progress: Number(fb.progress) });
      toast.success("Feedback submitted");
      setDialog(null); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const submitExtension = async () => {
    if (!ext.requested_due || !ext.reason) return toast.error("Date and reason required");
    try {
      await api.post(`/tasks/${id}/extension`, { ...ext, requested_due: new Date(ext.requested_due).toISOString() });
      toast.success("Extension requested"); setDialog(null); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const submitReassign = async () => {
    if (!reassign.new_owner) return toast.error("Select new owner");
    try {
      await api.post(`/tasks/${id}/reassign`, reassign);
      toast.success("Task reassigned"); setDialog(null); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const decideExtension = async (eid, decision) => {
    try { await api.post(`/tasks/${id}/extension/${eid}/decision`, { decision }); toast.success(`Extension ${decision}`); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const addComment = async () => {
    if (!comment.trim()) return;
    await api.post(`/tasks/${id}/comments`, { text: comment });
    setComment(""); load();
  };

  const setAssigneeStatus = async (s) => {
    await api.post(`/tasks/${id}/assignee-status`, { status: s });
    toast.success("Your status updated"); load();
  };

  const openTaskEdit = () => {
    setEf({
      title: t.title || "", ref_code: t.ref_code || "", customer_name: t.customer_name || "",
      description: t.description || "", priority: t.priority || "normal", department: t.department || "",
      due_at: t.due_at ? new Date(t.due_at).toISOString().slice(0, 16) : "",
      escalation_hours: t.escalation_hours || 2,
    });
    setDialog("editTask");
  };
  const saveTaskEdit = async () => {
    if (!ef.title?.trim()) return toast.error("Title required");
    try {
      await api.put(`/tasks/${id}`, { ...ef, due_at: ef.due_at ? new Date(ef.due_at).toISOString() : null });
      toast.success("Task updated"); setDialog(null); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const deleteTask = async () => {
    if (!window.confirm("Delete this task permanently?")) return;
    try { await api.delete(`/tasks/${id}`); toast.success("Task deleted"); navigate("/tasks"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const uploadFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Max 5MB");
    const reader = new FileReader();
    reader.onload = async () => {
      await api.post(`/tasks/${id}/attachments`, { name: file.name, type: file.type, size: file.size, data: reader.result });
      toast.success("Attached"); load();
    };
    reader.readAsDataURL(file);
  };

  const deleteAttachment = async (a) => {
    if (!window.confirm(`Remove ${a.name}?`)) return;
    try { await api.delete(`/tasks/${id}/attachments/${a.id}`); toast.success("File removed"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const time = t.time || {};
  const overdue = t.is_overdue && !["completed", "approved", "cancelled"].includes(t.status);

  return (
    <div className="p-6 lg:p-8 max-w-[1400px] animate-fade-in">
      <button onClick={() => navigate("/tasks")} className="text-sm text-zinc-500 hover:text-emerald-400 flex items-center gap-1 mb-4 transition-colors">
        <CaretLeft size={14} /> Back
      </button>

      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <span className="text-xs text-zinc-300">{t.customer_name || "—"}</span>
            {t.ref_code && <span className="font-mono text-xs text-emerald-600">{t.ref_code}</span>}
            <PriorityBadge priority={t.priority} />
            <StatusBadge status={overdue ? "overdue" : t.status} />
          </div>
          <h1 className="font-heading text-3xl font-black tracking-tight text-zinc-50">{t.title}</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          {isManager && (
            <Button onClick={openTaskEdit} variant="outline" data-testid="edit-task-btn" className="border-zinc-800 bg-zinc-900 rounded-sm gap-1"><ArrowsClockwise size={15} /> Edit</Button>
          )}
          {user.role === "admin" && (
            <Button onClick={deleteTask} variant="outline" data-testid="delete-task-btn" className="border-red-500/30 text-red-400 bg-red-500/5 rounded-sm gap-1"><X size={15} /> Delete</Button>
          )}
          {isManager && t.status === "submitted" && (
            <>
              <Button onClick={() => changeStatusApprove()} data-testid="approve-btn" className="bg-emerald-600 hover:bg-emerald-700 rounded-sm gap-1"><Check size={15} weight="bold" /> Approve</Button>
              <Button onClick={() => rejectTask()} variant="outline" data-testid="reject-btn" className="border-red-500/30 text-red-400 bg-red-500/5 rounded-sm gap-1"><X size={15} /> Reject</Button>
            </>
          )}
          {isManager && ["completed", "approved", "rejected"].includes(t.status) && (
            <Button onClick={() => changeStatus("reopened")} variant="outline" className="border-zinc-800 bg-zinc-900 rounded-sm gap-1"><ArrowsClockwise size={15} /> Reopen</Button>
          )}
          {isManager && <Button onClick={() => setDialog("reassign")} variant="outline" data-testid="reassign-open" className="border-zinc-800 bg-zinc-900 rounded-sm gap-1"><ArrowsClockwise size={15} /> Reassign</Button>}
        </div>
      </div>

      {/* countdown bar */}
      <div className={`flex items-center gap-6 p-4 border mb-6 ${overdue ? "border-red-500/30 bg-red-500/5" : "border-zinc-800 bg-zinc-900/50"}`}>
        <Clock size={22} className={overdue ? "text-red-400" : "text-emerald-400"} />
        <div className="flex gap-8 flex-wrap text-sm">
          <div><span className="text-zinc-500 text-xs uppercase tracking-wider block">Allowed</span><span className="font-mono text-zinc-200">{time.allowed || "—"}</span></div>
          <div><span className="text-zinc-500 text-xs uppercase tracking-wider block">Elapsed</span><span className="font-mono text-zinc-200">{time.elapsed || "—"}</span></div>
          <div><span className="text-zinc-500 text-xs uppercase tracking-wider block">{overdue ? "Delayed By" : "Remaining"}</span><span className={`font-mono ${overdue ? "text-red-400" : "text-emerald-400"}`}>{overdue ? time.delay : time.remaining || "—"}</span></div>
          <div><span className="text-zinc-500 text-xs uppercase tracking-wider block">Task Age</span><span className="font-mono text-zinc-200">{time.age || "—"}</span></div>
          <div><span className="text-zinc-500 text-xs uppercase tracking-wider block">Progress</span><span className="font-mono text-emerald-400">{t.progress || 0}%</span></div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: main context */}
        <div className="lg:col-span-2 space-y-6">
          {/* member actions */}
          {isAssignee && !["completed", "approved", "cancelled"].includes(t.status) && (
            <div className="border border-zinc-800 bg-zinc-900/30 p-4">
              <div className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-3">My Actions</div>
              <div className="flex flex-wrap gap-2">
                {STATUS_ACTIONS.member.map((a) => (
                  <Button key={a.s} size="sm" variant="outline" onClick={() => changeStatus(a.s)} data-testid={`action-${a.s}`} className="border-zinc-800 bg-zinc-950 hover:border-emerald-600 rounded-sm text-xs">{a.l}</Button>
                ))}
                <Button size="sm" onClick={() => setDialog("feedback")} data-testid="feedback-open" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm text-xs gap-1"><ChatCircleDots size={14} /> Add Feedback</Button>
                <Button size="sm" variant="outline" onClick={() => setDialog("extension")} data-testid="extension-open" className="border-zinc-800 bg-zinc-950 rounded-sm text-xs gap-1"><ClockClockwise size={14} /> Request Extension</Button>
                {t.assignment_type !== "common" && myAssignee && (
                  <Button size="sm" variant="outline" onClick={() => setAssigneeStatus("completed")} className="border-emerald-500/30 text-emerald-400 bg-emerald-500/5 rounded-sm text-xs gap-1"><Check size={14} /> Complete My Part</Button>
                )}
              </div>
            </div>
          )}

          {t.description && (
            <div className="border border-zinc-800 bg-zinc-900/30 p-5">
              <div className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Description</div>
              <p className="text-sm text-zinc-300 whitespace-pre-wrap">{t.description}</p>
              {t.instructions && <><div className="text-xs font-bold uppercase tracking-wider text-zinc-500 mt-4 mb-2">Instructions</div><p className="text-sm text-zinc-300 whitespace-pre-wrap">{t.instructions}</p></>}
            </div>
          )}

          <Tabs defaultValue="feedback">
            <TabsList className="bg-zinc-900 border border-zinc-800 rounded-sm">
              <TabsTrigger value="feedback" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white rounded-sm text-xs">Feedback</TabsTrigger>
              <TabsTrigger value="discussion" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white rounded-sm text-xs">Discussion</TabsTrigger>
              <TabsTrigger value="subtasks" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white rounded-sm text-xs">Subtasks</TabsTrigger>
              <TabsTrigger value="attachments" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white rounded-sm text-xs">Files</TabsTrigger>
              <TabsTrigger value="audit" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white rounded-sm text-xs">Audit</TabsTrigger>
            </TabsList>

            <TabsContent value="feedback" className="mt-4 space-y-3">
              {(t.feedback || []).length === 0 && <div className="text-sm text-zinc-500 py-4">No feedback yet. Mandatory before submitting.</div>}
              {(t.feedback || []).map((fbk) => (
                <div key={fbk.id} className="border border-zinc-800 bg-zinc-900/30 p-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-zinc-200 font-medium">{fbk.user_name}</span>
                    <span className="text-xs font-mono text-zinc-500">{fmtDate(fbk.at)}</span>
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="flex-1 h-1.5 bg-zinc-800 rounded-full overflow-hidden"><div className="h-full bg-emerald-600" style={{ width: `${fbk.progress}%` }} /></div>
                    <span className="text-xs font-mono text-emerald-400">{fbk.progress}%</span>
                  </div>
                  {fbk.completed && <p className="text-xs text-zinc-400"><b className="text-zinc-300">Done:</b> {fbk.completed}</p>}
                  {fbk.pending && <p className="text-xs text-zinc-400"><b className="text-zinc-300">Pending:</b> {fbk.pending}</p>}
                  {fbk.blockers && <p className="text-xs text-red-400"><b>Blocker:</b> {fbk.blockers}</p>}
                  {fbk.remarks && <p className="text-xs text-zinc-400 mt-1">{fbk.remarks}</p>}
                </div>
              ))}
            </TabsContent>

            <TabsContent value="discussion" className="mt-4">
              <div className="space-y-3 mb-4">
                {(t.comments || []).length === 0 && <div className="text-sm text-zinc-500 py-4">No messages yet.</div>}
                {(t.comments || []).map((c) => (
                  <div key={c.id} className="flex gap-3">
                    <div className="w-8 h-8 rounded-sm bg-zinc-800 flex items-center justify-center text-emerald-400 text-sm font-bold shrink-0">{c.user_name?.[0]}</div>
                    <div className="flex-1 bg-zinc-900/30 border border-zinc-800 p-3">
                      <div className="flex justify-between mb-1"><span className="text-sm text-zinc-200">{c.user_name}</span><span className="text-xs font-mono text-zinc-500">{fmtDate(c.at)}</span></div>
                      <p className="text-sm text-zinc-300">{c.text}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Input value={comment} onChange={(e) => setComment(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addComment()} placeholder="Write a message…" className={inputCls} data-testid="comment-input" />
                <Button onClick={addComment} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm" data-testid="comment-send"><PaperPlaneRight size={16} weight="fill" /></Button>
              </div>
            </TabsContent>

            <TabsContent value="subtasks" className="mt-4 space-y-2">
              <Button size="sm" onClick={() => navigate(`/tasks/new?parent=${id}`)} className="bg-zinc-900 border border-zinc-800 hover:border-emerald-600 text-zinc-200 rounded-sm text-xs gap-1 mb-2"><TreeStructure size={14} /> Add Subtask</Button>
              {(t.subtasks || []).length === 0 && <div className="text-sm text-zinc-500">No subtasks.</div>}
              {(t.subtasks || []).map((s) => (
                <button key={s.id} onClick={() => navigate(`/tasks/${s.id}`)} className="w-full flex items-center gap-3 border-l-2 border-zinc-700 pl-3 py-2 hover:bg-zinc-900 transition-colors text-left">
                  <span className="font-mono text-[11px] text-zinc-500">{s.ref_code || s.customer_name || "—"}</span>
                  <span className="flex-1 text-sm text-zinc-200 truncate">{s.title}</span>
                  <StatusBadge status={s.status} />
                </button>
              ))}
            </TabsContent>

            <TabsContent value="attachments" className="mt-4 space-y-2">
              <label className="inline-flex items-center gap-2 border border-zinc-800 bg-zinc-900 hover:border-emerald-600 px-3 py-2 text-sm text-zinc-200 cursor-pointer rounded-sm transition-colors" data-testid="upload-label">
                <Paperclip size={16} /> Upload File<input type="file" className="hidden" onChange={uploadFile} />
              </label>
              <div className="mt-2 space-y-1">
                {(t.attachments || []).map((a) => (
                  <div key={a.id} className="flex items-center gap-2 text-sm text-zinc-300 border border-zinc-800 bg-zinc-900/30 px-3 py-2 transition-colors" data-testid={`task-att-${a.id}`}>
                    <Paperclip size={14} className="shrink-0" />
                    <span className="truncate flex-1">{a.name}</span>
                    <span className="text-xs text-zinc-500">{a.uploaded_by}</span>
                    <button type="button" onClick={() => viewAttachmentFile(a, toast)} className="text-xs font-semibold text-emerald-400 hover:text-emerald-300 shrink-0" data-testid={`task-att-view-${a.id}`}>View</button>
                    <a href={a.data} download={a.name} className="text-xs font-semibold text-zinc-400 hover:text-zinc-200 shrink-0" data-testid={`task-att-download-${a.id}`}>Download</a>
                    <button type="button" onClick={() => deleteAttachment(a)} className="text-xs font-semibold text-red-400 hover:text-red-300 shrink-0" data-testid={`task-att-del-${a.id}`}>Delete</button>
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="audit" className="mt-4">
              <div className="border-l border-zinc-800 ml-2 space-y-3">
                {(t.audit || []).map((a) => (
                  <div key={a.id} className="relative pl-5">
                    <span className="absolute -left-[5px] top-1.5 w-2 h-2 bg-emerald-600 rounded-full" />
                    <div className="text-sm text-zinc-200">{a.action.replace(/_/g, " ")}</div>
                    <div className="text-xs font-mono text-zinc-500">{a.user_name} · {fmtDate(a.at)}</div>
                  </div>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Right: metadata */}
        <div className="space-y-4">
          <div className="border border-zinc-800 bg-zinc-900/30 p-4">
            <div className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Details</div>
            <Meta label="Department">{t.department || "—"}</Meta>
            <Meta label="Project / Customer">{t.project || "—"}</Meta>
            <Meta label="Ref Code">{t.ref_code || "—"}</Meta>
            <Meta label="Customer Name">{t.customer_name || "—"}</Meta>
            {t.custom && Object.entries(t.custom).map(([k, v]) => (
              v ? <Meta key={k} label={k.replace(/_/g, " ")}>{v}</Meta> : null
            ))}
            <Meta label="Category">{t.category || "—"}</Meta>
            <Meta label="Assignment Type"><span className="capitalize">{t.assignment_type?.replace(/_/g, " ")}</span></Meta>
            <Meta label="Created By">{t.created_by_name}</Meta>
            <Meta label="Created">{fmtDate(t.created_at)}</Meta>
            <Meta label="Start">{fmtDate(t.start_at)}</Meta>
            <Meta label="Due">{fmtDate(t.due_at)}</Meta>
            {t.acknowledged_at && <Meta label="Acknowledged">{fmtDate(t.acknowledged_at)}</Meta>}
            {t.started_at && <Meta label="Started">{fmtDate(t.started_at)}</Meta>}
            {t.completed_at && <Meta label="Completed">{fmtDate(t.completed_at)}</Meta>}
          </div>

          <div className="border border-zinc-800 bg-zinc-900/30 p-4">
            <div className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-3">Members & Responsibilities</div>
            <div className="space-y-2">
              {(t.assignees || []).map((a) => (
                <div key={a.user_id} className="flex items-start gap-2">
                  <div className="w-7 h-7 rounded-sm bg-zinc-800 flex items-center justify-center text-emerald-400 text-xs font-bold shrink-0">{a.name?.[0]}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-zinc-200">{a.name}</div>
                    {a.responsibility && <div className="text-xs text-zinc-500">{a.responsibility}</div>}
                    {a.deadline && <div className="text-[10px] font-mono text-zinc-600">Due {fmtDate(a.deadline)}</div>}
                  </div>
                  <StatusBadge status={a.status || "assigned"} />
                </div>
              ))}
            </div>
          </div>

          {(t.dependency_details || []).length > 0 && (
            <div className="border border-zinc-800 bg-zinc-900/30 p-4">
              <div className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-3 flex items-center gap-1"><GitBranch size={14} /> Dependencies</div>
              {t.dependency_details.map((d) => (
                <button key={d.id} onClick={() => navigate(`/tasks/${d.id}`)} className="w-full flex items-center gap-2 py-1.5 text-left hover:text-emerald-400 transition-colors">
                  {["completed", "approved"].includes(d.status) ? <Check size={14} className="text-emerald-400" /> : <Warning size={14} className="text-amber-400" />}
                  <span className="text-xs text-zinc-300 truncate flex-1">{d.title}</span>
                  <StatusBadge status={d.status} />
                </button>
              ))}
            </div>
          )}

          {(t.extensions || []).length > 0 && (
            <div className="border border-zinc-800 bg-zinc-900/30 p-4">
              <div className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-3">Extension Requests</div>
              {t.extensions.map((e) => (
                <div key={e.id} className="border-b border-zinc-800 last:border-0 py-2">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-zinc-300">{e.requested_by_name} → {fmtDate(e.requested_due)}</span>
                    <StatusBadge status={e.status === "pending" ? "extension_requested" : e.status === "approved" ? "approved" : "rejected"} />
                  </div>
                  <p className="text-xs text-zinc-500 mt-1">{e.reason}</p>
                  {isManager && e.status === "pending" && (
                    <div className="flex gap-2 mt-2">
                      <Button size="sm" onClick={() => decideExtension(e.id, "approve")} className="h-7 bg-emerald-600 hover:bg-emerald-700 rounded-sm text-xs">Approve</Button>
                      <Button size="sm" variant="outline" onClick={() => decideExtension(e.id, "reject")} className="h-7 border-red-500/30 text-red-400 rounded-sm text-xs">Reject</Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Feedback Dialog */}
      <Dialog open={dialog === "feedback"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">Progress Feedback</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            <div><label className={label}>Progress: {fb.progress}%</label><input type="range" min="0" max="100" value={fb.progress} onChange={(e) => setFb({ ...fb, progress: e.target.value })} className="w-full accent-emerald-600" data-testid="feedback-progress" /></div>
            <div><label className={label}>Work Completed</label><Textarea value={fb.completed} onChange={(e) => setFb({ ...fb, completed: e.target.value })} className={inputCls} rows={2} data-testid="feedback-completed" /></div>
            <div><label className={label}>Work Pending</label><Textarea value={fb.pending} onChange={(e) => setFb({ ...fb, pending: e.target.value })} className={inputCls} rows={2} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={label}>Time Spent</label><Input value={fb.time_spent} onChange={(e) => setFb({ ...fb, time_spent: e.target.value })} className={inputCls} placeholder="e.g. 3h" /></div>
              <div><label className={label}>Next Action</label><Input value={fb.next_action} onChange={(e) => setFb({ ...fb, next_action: e.target.value })} className={inputCls} /></div>
            </div>
            <div><label className={label}>Problems / Blockers</label><Textarea value={fb.blockers} onChange={(e) => setFb({ ...fb, blockers: e.target.value })} className={inputCls} rows={2} /></div>
            <div><label className={label}>Remarks</label><Textarea value={fb.remarks} onChange={(e) => setFb({ ...fb, remarks: e.target.value })} className={inputCls} rows={2} /></div>
          </div>
          <DialogFooter><Button onClick={submitFeedback} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm" data-testid="feedback-submit">Submit Feedback</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Extension Dialog */}
      <Dialog open={dialog === "extension"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
          <DialogHeader><DialogTitle className="font-heading">Request Extension</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className={label}>Requested New Due Date</label><input type="datetime-local" value={ext.requested_due} onChange={(e) => setExt({ ...ext, requested_due: e.target.value })} className="w-full bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 text-sm [color-scheme:dark]" data-testid="ext-date" /></div>
            <div><label className={label}>Reason</label><Textarea value={ext.reason} onChange={(e) => setExt({ ...ext, reason: e.target.value })} className={inputCls} rows={2} data-testid="ext-reason" /></div>
            <div><label className={label}>Pending Work</label><Textarea value={ext.pending_work} onChange={(e) => setExt({ ...ext, pending_work: e.target.value })} className={inputCls} rows={2} /></div>
          </div>
          <DialogFooter><Button onClick={submitExtension} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm" data-testid="ext-submit">Send Request</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reassign Dialog */}
      <Dialog open={dialog === "reassign"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
          <DialogHeader><DialogTitle className="font-heading">Reassign Task</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className={label}>New Primary Owner</label>
              <Select value={reassign.new_owner} onValueChange={(v) => setReassign({ ...reassign, new_owner: v })}>
                <SelectTrigger className={inputCls} data-testid="reassign-owner"><SelectValue placeholder="Select member" /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                  {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><label className={label}>Reason</label><Textarea value={reassign.reason} onChange={(e) => setReassign({ ...reassign, reason: e.target.value })} className={inputCls} rows={2} /></div>
          </div>
          <DialogFooter><Button onClick={submitReassign} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm" data-testid="reassign-submit">Reassign</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog === "editTask"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-50 max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">Edit Task</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
            <div><label className={label}>Title *</label><Input value={ef.title || ""} onChange={(e) => setEf({ ...ef, title: e.target.value })} className={inputCls} data-testid="edit-task-title" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={label}>Ref Code</label><Input value={ef.ref_code || ""} onChange={(e) => setEf({ ...ef, ref_code: e.target.value })} className={inputCls} /></div>
              <div><label className={label}>Customer Name</label><Input value={ef.customer_name || ""} onChange={(e) => setEf({ ...ef, customer_name: e.target.value })} className={inputCls} /></div>
            </div>
            <div><label className={label}>Description</label><Textarea value={ef.description || ""} onChange={(e) => setEf({ ...ef, description: e.target.value })} className={inputCls} rows={2} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={label}>Priority</label>
                <Select value={ef.priority} onValueChange={(v) => setEf({ ...ef, priority: v })}>
                  <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-50">{["low", "normal", "high", "urgent", "critical"].map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><label className={label}>Escalate after (hrs overdue)</label><Input type="number" value={ef.escalation_hours} onChange={(e) => setEf({ ...ef, escalation_hours: +e.target.value })} className={inputCls} /></div>
            </div>
            <div><label className={label}>Due Date & Time</label><input type="datetime-local" value={ef.due_at || ""} onChange={(e) => setEf({ ...ef, due_at: e.target.value })} className="w-full bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 text-sm [color-scheme:dark]" /></div>
          </div>
          <DialogFooter><Button onClick={saveTaskEdit} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm" data-testid="edit-task-save">Save Changes</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );

  async function changeStatusApprove() {
    try { await api.post(`/tasks/${id}/approve`, { note: "Approved" }); toast.success("Task approved & completed"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  }
  async function rejectTask() {
    try { await api.post(`/tasks/${id}/reject`, { note: "Needs rework" }); toast.success("Task rejected"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  }
}
